"use client";

import React from "react";

export type TaraCategory =
  | "Janma"
  | "Sampat"
  | "Vipat"
  | "Kshema"
  | "Pratyak"
  | "Sadhana"
  | "Naidhana"
  | "Mitra"
  | "ParamaMitra";

export interface PersonalTaraBalamData {
  birthNakshatraEn: string;
  birthNakshatraHi: string;
  transitNakshatraEn: string;
  transitNakshatraHi: string;
  taraNameEn: string;
  taraNameHi: string;
  isAuspicious: boolean;
  scoreOutOf9: number;
  personalGreenHours: Array<{
    start: string;
    end: string;
    purposeEn: string;
    purposeHi: string;
  }>;
  adviceEn: string;
  adviceHi: string;
}

export interface PersonalTaraBalamCardProps {
  data: PersonalTaraBalamData;
  locale?: "en" | "hi";
}

export default function PersonalTaraBalamCard({
  data,
  locale = "en",
}: PersonalTaraBalamCardProps) {
  const isHi = locale === "hi";

  return (
    <div
      className="w-full rounded-2xl border p-5 sm:p-6 transition-all bg-surface-raised border-border-default shadow-card"
      aria-label={isHi ? "दैनिक व्यक्तिगत ताराबल एवं शुभ मुहूर्त" : "Personal Daily Tara Balam & Auspicious Hours"}
    >
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <div>
          <div className="flex items-center gap-2">
            <span className="text-xl">✨</span>
            <h3 className="text-base sm:text-lg font-semibold tracking-tight text-text-primary">
              {isHi ? "व्यक्तिगत दैनिक ताराबल (Personal Timing)" : "Personal Daily Tara Balam"}
            </h3>
          </div>
          <p className="text-xs sm:text-sm mt-0.5 text-text-secondary">
            {isHi
              ? `जन्म नक्षत्र: ${data.birthNakshatraHi} ➔ आज का नक्षत्र: ${data.transitNakshatraHi}`
              : `Birth Star: ${data.birthNakshatraEn} ➔ Today's Star: ${data.transitNakshatraEn}`}
          </p>
        </div>

        {/* Tara Status Badge */}
        <div
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold self-start sm:self-auto ${
            data.isAuspicious
              ? "bg-success/15 text-success border border-success/40"
              : "bg-error/15 text-error border border-error/40"
          }`}
        >
          <span>{data.isAuspicious ? "🟢" : "🔴"}</span>
          <span>{isHi ? data.taraNameHi : data.taraNameEn}</span>
          <span className="text-caption opacity-80">
            {data.isAuspicious
              ? isHi
                ? "(शुभ तारा)"
                : "(Auspicious)"
              : isHi
              ? "(सावधानी)"
              : "(Inauspicious)"}
          </span>
        </div>
      </div>

      {/* Daily Advice */}
      <div className="p-3.5 rounded-xl border mb-4 text-xs sm:text-sm bg-surface-canvas border-border-subtle">
        <p className="text-text-primary leading-relaxed">
          {isHi ? data.adviceHi : data.adviceEn}
        </p>
      </div>

      {/* Personal 3 Green Hours */}
      {data.personalGreenHours.length > 0 && (
        <div className="mt-4">
          <h4 className="text-xs font-bold uppercase tracking-wider text-text-secondary mb-2.5 flex items-center gap-1.5">
            <span>⏳</span>
            {isHi
              ? "आज आपके लिए श्रेष्ठ व्यक्तिगत मुहूर्त (Green Hours):"
              : "Your Personalized Auspicious Windows Today:"}
          </h4>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            {data.personalGreenHours.map((window, idx) => (
              <div
                key={idx}
                className="p-3 rounded-xl border bg-success/10 border-success/30 flex flex-col justify-between"
              >
                <div className="flex items-center justify-between text-xs mb-1">
                  <span className="font-bold text-success font-mono">
                    {window.start} – {window.end}
                  </span>
                  <span className="text-caption px-1.5 py-0.2 bg-success/20 text-success font-semibold rounded">
                    {isHi ? "शुभ" : "Ideal"}
                  </span>
                </div>
                <span className="text-caption text-text-secondary">
                  {isHi ? window.purposeHi : window.purposeEn}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
